import {createUseStyles} from 'react-jss'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {useSelector} from 'react-redux'
import Button from '../../../Button'
import {UpcomingPayroll} from '../../../../redux/types'
import {RootState} from '../../../../redux/store'
import UploadFileModal from '../../../UploadFileModal'
import CreatePayrollFileRowsModal from '../../../Payroll/CreatePayrollFileRowsModal'
import {useFileUploadModal, useCreatePayrollFileRowsModal} from '../hooks'
import usePayrollActions from '../../../../hooks/usePayrollActions'
import useFileActions from "../../../../hooks/useFileActions";
import ConfirmModal from "../../../ConfirmModal";
import {useState} from "react";

interface AddButtonsProps {
  upcomingPayroll: UpcomingPayroll
}

const PayrollActions = ({
                      upcomingPayroll
                    }: AddButtonsProps) => {
  const styles = useStyles()
  const {t} = useTranslation(['common', 'payrolls'])
  const navigate = useNavigate()
  const customer = useSelector((state: RootState) => state.customer.customer)

  const [confirmModalVisible, setConfirmModalVisible] = useState(false)
  const [confirmManualRowsModalVisible, setConfirmManualRowsModalVisible] = useState(false)

  const fileUploadModal = useFileUploadModal(upcomingPayroll)
  const createRowsModal = useCreatePayrollFileRowsModal(upcomingPayroll)
  const {startPayroll} = usePayrollActions()
  const {getFiles} = useFileActions()

  const getUploadButtonText = () => {
    if (upcomingPayroll.validationErrors?.length > 0) {
      return t('uploadAnotherPayrollFile', {ns: 'payrolls'})
    } else if (upcomingPayroll.payrollFileSubmitted) {
      return t('uploadAnotherPayrollFile', {ns: 'payrolls'})
    } else {
      return t('uploadPayrollFile', {ns: 'payrolls'})
    }
  }

  const getUploadButtonVariant = () => {
    const hasSubmittedFile = upcomingPayroll.payrollFileSubmitted || upcomingPayroll.validationErrors?.length > 0
    return hasSubmittedFile ? 'negative' : 'positive'
  }

  const onUploadButtonClick = () => {
    if (upcomingPayroll.payrollNotYetDue && !upcomingPayroll.payrollCreated) {
      setConfirmModalVisible(true)
      return
    }
    onConfirmUpload()
  }

  const onConfirmUpload = () => {
    if (customer?.employer && customer.employer.enablePayrollFileAdjustment) {
      navigate(`/payroll/create?payday=${upcomingPayroll.paymentDate}`)
    } else {
      fileUploadModal.show()
    }
  }

  const onCreateManualButtonClick = () => {
    if (upcomingPayroll.payrollNotYetDue && !upcomingPayroll.payrollCreated) {
      setConfirmManualRowsModalVisible(true)
      return
    }
    onConfirmManual()
  }

  const onConfirmManual = () => {
    createRowsModal.show()
    setConfirmManualRowsModalVisible(false)
  }

  const onStartPayrollButtonClick = () => {
    if (upcomingPayroll.payrollNotYetDue && !upcomingPayroll.payrollCreated) {
      setConfirmModalVisible(true)
      return
    }
    onConfirmPayrollStart()
  }

  const onConfirmPayrollStart = () => {
    startPayroll(upcomingPayroll.paymentDate)
      .then(() => {
        return getFiles()
      })
    setConfirmModalVisible(false)
  }

  if (!customer?.employer?.enablePayrollFileAdjustment && !upcomingPayroll.payrollCreated) {
    // In this case, the user must start the payroll process before uploading a file.
    // This gives them the chance to download a bank account import file, which they
    // need to import before exporting a payroll file.
    return (
      <>
        <div className={styles.fileUploadButtonContainer}>
          <Button
            title={t('startPayroll', {ns: 'payrolls'})}
            onClick={onStartPayrollButtonClick}
          />
        </div>
        <ConfirmModal
          visible={confirmModalVisible}
          confirmText={t('confirmStartEarlyPayroll', {ns: 'payrolls'})}
          onConfirmClick={onConfirmPayrollStart}
          onCancelClick={() => setConfirmModalVisible(false)}
        />
      </>
    )
  }

  return (
    <>
      <div className={styles.fileUploadButtonContainer}>
        {upcomingPayroll.payrollFileSubmitted &&
            <h6>{t('uploadAnotherPayrollFileIntro', {ns: 'payrolls'})}</h6>}
        <Button
          title={getUploadButtonText()}
          variant={getUploadButtonVariant() ? 'negative' : 'positive'}
          onClick={onUploadButtonClick}
        />
        {customer?.employer?.manualPayrollFileRowCreationEnabled &&
            <Button
                title={t('createPayrollFileRowsManually', {ns: 'payrolls'})}
                variant={'negative'}
                onClick={onCreateManualButtonClick}
            />
        }
      </div>

      <ConfirmModal
        visible={confirmModalVisible}
        confirmText={t('confirmStartEarlyPayroll', {ns: 'payrolls'})}
        onConfirmClick={onConfirmUpload}
        onCancelClick={() => setConfirmModalVisible(false)}
      />

      <ConfirmModal
        visible={confirmManualRowsModalVisible}
        confirmText={t('confirmStartEarlyPayroll', {ns: 'payrolls'})}
        onConfirmClick={onConfirmManual}
        onCancelClick={() => setConfirmManualRowsModalVisible(false)}
      />

      <UploadFileModal
        visible={fileUploadModal.visible}
        multiFileUpload={false}
        title={t('uploadPayrollFile', {ns: 'payrolls'})}
        onCancelClick={fileUploadModal.hide}
        onUpload={(files) => fileUploadModal.handleUpload(files[0])}
      />

      <CreatePayrollFileRowsModal
        visible={createRowsModal.visible}
        onCreateClick={createRowsModal.handleCreate}
        onCloseClick={createRowsModal.hide}
      />
    </>
  )
}

const useStyles = createUseStyles({
  fileUploadButtonContainer: {
    marginTop: 40,
    display: 'flex',
    flex: 1,
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    rowGap: 6
  }
})

export default PayrollActions
